// ── Enums / string unions ──────────────────────────────────────────────────────

export type BankTransferWorkflowStatus =
  | 'Draft'
  | 'Submitted'
  | 'PendingApproval'
  | 'Approved'
  | 'Rejected'
  | 'Posted'
  | 'Cancelled';

export type BankTransferPostingStatus = 'NotPosted' | 'Posted' | 'Cancelled';

// ── Status display helpers ────────────────────────────────────────────────────

export const BNK_WORKFLOW_STATUS_LABELS: Record<BankTransferWorkflowStatus, string> = {
  Draft:          'Draft',
  Submitted:      'Submitted',
  PendingApproval:'Pending Approval',
  Approved:       'Approved',
  Rejected:       'Rejected',
  Posted:         'Posted',
  Cancelled:      'Cancelled',
};

export const BNK_WORKFLOW_STATUS_COLORS: Record<BankTransferWorkflowStatus, string> = {
  Draft:          'bg-gray-100 text-gray-700',
  Submitted:      'bg-blue-100 text-blue-700',
  PendingApproval:'bg-yellow-100 text-yellow-700',
  Approved:       'bg-green-100 text-green-700',
  Rejected:       'bg-red-100 text-red-700',
  Posted:         'bg-purple-100 text-purple-700',
  Cancelled:      'bg-gray-200 text-gray-500',
};

// ── Detail DTO ────────────────────────────────────────────────────────────────

export interface BankTransferDto {
  id:                    string;
  companyId:             string;
  locationId?:           string | null;
  costCenterId?:         string | null;
  financialYearCode:     string;
  financialPeriodCode:   string;
  transferNumber:        string;
  transferDate:          string;
  postingDate:           string;
  fromBankAccountId:     string;
  fromAccountCode:       string;
  fromAccountName:       string;
  fromGLAccountId?:      string | null;
  toBankAccountId:       string;
  toAccountCode:         string;
  toAccountName:         string;
  toGLAccountId?:        string | null;
  currencyCode:          string;
  exchangeRate:          number;
  amount:                number;
  baseAmount:            number;
  reference?:            string | null;
  narration?:            string | null;
  workflowStatus:        BankTransferWorkflowStatus;
  postingStatus:         BankTransferPostingStatus;
  submittedBy?:          string | null;
  submittedDate?:        string | null;
  approvedBy?:           string | null;
  approvedDate?:         string | null;
  rejectedBy?:           string | null;
  rejectedDate?:         string | null;
  rejectionReason?:      string | null;
  postedBy?:             string | null;
  postedDate?:           string | null;
  cancelledBy?:          string | null;
  cancelledDate?:        string | null;
  cancellationReason?:   string | null;
  isReversal:            boolean;
  originalTransferId?:   string | null;
  reversalTransferId?:   string | null;
  sourceGlJournalId?:    string | null;
  createdDate:           string;
}

// ── List row DTO ──────────────────────────────────────────────────────────────

export interface BankTransferListDto {
  id:              string;
  transferNumber:  string;
  transferDate:    string;
  postingDate:     string;
  fromAccountCode: string;
  fromAccountName: string;
  toAccountCode:   string;
  toAccountName:   string;
  currencyCode:    string;
  amount:          number;
  baseAmount:      number;
  workflowStatus:  BankTransferWorkflowStatus;
  postingStatus:   BankTransferPostingStatus;
  reference?:      string | null;
  isReversal:      boolean;
  createdDate:     string;
}

// ── Filter DTO ────────────────────────────────────────────────────────────────

export interface BankTransferFilterDto {
  companyId?:          string;
  locationId?:         string;
  costCenterId?:       string;
  financialYearCode?:  string;
  fromBankAccountId?:  string;
  toBankAccountId?:    string;
  workflowStatus?:     string;
  postingStatus?:      string;
  dateFrom?:           string;
  dateTo?:             string;
  search?:             string;
  pageNumber?:         number;
  pageSize?:           number;
  sortBy?:             string;
  sortDescending?:     boolean;
}

// ── Create / Update requests ──────────────────────────────────────────────────

export interface CreateBankTransferRequest {
  companyId:           string;
  locationId?:         string | null;
  costCenterId?:       string | null;
  financialYearCode:   string;
  financialPeriodCode: string;
  transferDate:        string;
  postingDate:         string;
  fromBankAccountId:   string;
  toBankAccountId:     string;
  currencyCode:        string;
  exchangeRate:        number;
  amount:              number;
  reference?:          string | null;
  narration?:          string | null;
}

export interface UpdateBankTransferRequest {
  transferDate:      string;
  postingDate:       string;
  fromBankAccountId: string;
  fromAccountCode:   string;
  fromAccountName:   string;
  fromGLAccountId?:  string | null;
  toBankAccountId:   string;
  toAccountCode:     string;
  toAccountName:     string;
  toGLAccountId?:    string | null;
  currencyCode:      string;
  exchangeRate:      number;
  amount:            number;
  reference?:        string | null;
  narration?:        string | null;
}
